import type { FieldValues } from "react-hook-form";

/**
 * Источники начального snapshot для `BusinessForm`.
 *
 * @template TFieldValues тип значений формы
 */
export interface ResolveBusinessFormInitialValuesOptions<TFieldValues extends FieldValues> {
    /**
     * Авторитетный snapshot формы. \
     * При наличии полностью отключает `seedValues`, submission memory и defaults.
     */
    initialValues?: TFieldValues;
    /**
     * Контекстные значения автозаполнения (session defaults). \
     * Применяются только к полям без remembered value: осознанный выбор пользователя важнее дефолта.
     */
    seedValues?: Partial<TFieldValues> | null;
    /** Последние успешно отправленные значения с наивысшим приоритетом fallback-ветки. */
    rememberedValues?: Partial<TFieldValues> | null;
    /** Полный fallback из `formConfig.defaults()`. */
    defaultValues: TFieldValues;
}

/**
 * Накладывает только инициализированные поля источника.
 *
 * `null`, `""`, `false`, `0` и `[]` считаются значениями; fallback разрешает только `undefined`.
 *
 * @template TFieldValues тип значений формы
 * @param target целевой полный snapshot
 * @param source partial-источник значений
 * @returns тот же `target` после merge
 */
function applyInitializedValues<TFieldValues extends FieldValues>(
    target: TFieldValues,
    source: Partial<TFieldValues> | null | undefined,
): TFieldValues {
    if (source == null) {
        return target;
    }

    for (const fieldName in source) {
        if (!Object.prototype.hasOwnProperty.call(source, fieldName)) {
            continue;
        }

        const value = source[fieldName];
        if (value !== undefined) {
            target[fieldName] = value;
        }
    }

    return target;
}

/**
 * Вычисляет единственный initial snapshot до mount RHF.
 *
 * Приоритет fallback-ветки по полю: `rememberedValues` → `seedValues` → `defaultValues`.
 *
 * @template TFieldValues тип значений формы
 * @param options источники начальных значений
 * @returns полный snapshot формы
 */
export function resolveBusinessFormInitialValues<TFieldValues extends FieldValues>(
    options: ResolveBusinessFormInitialValuesOptions<TFieldValues>,
): TFieldValues {
    if (options.initialValues !== undefined) {
        return options.initialValues;
    }

    const resolved = { ...options.defaultValues };
    applyInitializedValues(resolved, options.seedValues);
    applyInitializedValues(resolved, options.rememberedValues);
    return resolved;
}
